/**
 * NOTE!:
 * Due to some bugs and problems with creating pdf file on docker environment and version 1.5.3 
 * (https://stackoverflow.com/questions/54748775/jspdf-error-failed-to-load-pdf-document-in-chrome) 
 * it had to be a rollback to 1.4.1 as a workaround 
 * (https://github.com/MrRio/jsPDF/issues/2208 - a different main problem but explained why can't be a rollback to 1.5.2)
 */

// Dependencies
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import 'jspdf-customfonts';

// Types
import { rgbArrayColors } from '../types/Color.types';

// Images
import ccLogoBase64 from '../assets/img/cc_logo';

// Utils
import { convertToTime } from '../utils/time-utils';

const pdfTypoSizes = {
    mainTitle: 25,
    sectionTitle: 21,
    text: 12,
    textUrl: 10
}

const drawHeader = (pdfInstance, translations) => {
    // Text/Data
    const URL = window.location.href;
    const mainTitle = String(translations('header.title')).toUpperCase();
    const imgData = ccLogoBase64;

    // Main Blue rectangle
    pdfInstance.setDrawColor(0);
    pdfInstance.setFillColor(...rgbArrayColors.primary);
    pdfInstance.rect(0, 0, 140, 40, 'F');

    // Main Title Text
    pdfInstance.setFontSize(pdfTypoSizes.mainTitle);
    pdfInstance.setTextColor(...rgbArrayColors.white);
    pdfInstance.text(mainTitle, 13, 18);

    // URL Text
    pdfInstance.setFontSize(pdfTypoSizes.textUrl);
    pdfInstance.setTextColor(...rgbArrayColors.white);
    pdfInstance.text(URL, 13, 29);
    // pdfInstance.splitTextToSize(URL, 135);

    // White left rectangle
    pdfInstance.setDrawColor(0);
    pdfInstance.setFillColor(...rgbArrayColors.white);
    pdfInstance.rect(5, 5, 1, 30, 'F');

    // Logo
    pdfInstance.addImage(imgData, 'PNG', 150, 10, 50, 11);
}

const drawUserSelection = (pdfInstance, translations, userSelection) => {
    // Text/Data
    const sectionText = translations('pdf.userSelection.sectionTitle');
    const userSelectionEntries = Object.entries(userSelection);

    // Section Title Text
    pdfInstance.setFontSize(pdfTypoSizes.sectionTitle);
    pdfInstance.setTextColor(...rgbArrayColors.black);
    pdfInstance.text(sectionText, 13, 60);

    // Section underscore
    pdfInstance.setDrawColor(0);
    pdfInstance.setFillColor(...rgbArrayColors.primary);
    pdfInstance.rect(12, 63, 53, 1, 'F');
    
    // Selection names and values table
    pdfInstance.autoTable({
        startY: 69,
        head: [
            [
                translations('pdf.userSelection.table.headers.key'),
                translations('pdf.userSelection.table.headers.value'),
            ],
        ],
        body: userSelectionEntries.reduce(
            (bodyAcc, [ selectionKey, selectionValue ]) => [
                ...bodyAcc, 
                [
                    translations(`form.fields.${selectionKey}.label`),
                    Number(selectionValue) 
                        ? selectionValue 
                        : translations(`form.fields.${selectionKey}.optionLabels.${selectionValue}`)
                ]
            ]
        , []),
        tableWidth: 70,
        columnStyles: {
            0: {
                cellWidth: 40
            }
        },
        margin: {
            left: 13
        },
        headStyles: {
            fillColor: rgbArrayColors.primary
        },
        styles: {
            ...getTableLatoFont(pdfInstance)
        }
    });
}

const drawPayouts = (pdfInstance, translations, payoutValues, language, currency, startY) => {
    // Utils Fn
    const eligibleForCurrencySign = (currency) => {
        /** IMPORTANT NOTE:
         * List of supported currencies is based on
         * capability of custom Lato font to proper 
         * render currency sign in pdf file
         * */
        const listOfSupportedCurrencies = ['USD', 'GBP', 'EUR', 'PLN'];
        return listOfSupportedCurrencies.includes(currency);
    }
    const getPayoutWithCurrency = (payout, language, currency) => {
        return eligibleForCurrencySign(currency)
            ? Number(payout).toLocaleString(language, { style: 'currency', currency })
            : `${payout} ${currency.toUpperCase()}`;
    }

    // Text/Data
    const sectionText = translations('results.tournamentPayouts.cardTitle'); 

    // Section Title Text
    pdfInstance.setFontSize(pdfTypoSizes.sectionTitle);
    pdfInstance.setTextColor(...rgbArrayColors.black);
    pdfInstance.text(sectionText, 13, startY);

    // Section underscore
    pdfInstance.setDrawColor(0);
    pdfInstance.setFillColor(...rgbArrayColors.primary);
    pdfInstance.rect(12, startY + 3, 71, 1, 'F');

    // Payout values table
    pdfInstance.autoTable({
        startY: startY + 9,
        head: [
            [
                translations('pdf.tournamentPayouts.table.headers.key'),
                translations('pdf.tournamentPayouts.table.headers.value'),
            ],
        ],
        body: payoutValues.reduce(
            (bodyAcc, payout, payoutIndex) => [
                ...bodyAcc, 
                [
                    payoutIndex + 1,
                    getPayoutWithCurrency(payout, language, currency)
                ]
            ]
        , []),
        tableWidth: 70,
        columnStyles: {
            0: {
                cellWidth: 15
            }
        },
        margin: {
            left: 13
        },
        headStyles: {
            fillColor: rgbArrayColors.primary
        },
        styles: {
            ...getTableLatoFont(pdfInstance)
        }
    });
}

const drawChipDistribution = (pdfInstance, translations, chipDistribution, left) => {
    // Text/Data
    const sectionText = translations('results.chipDistribution.cardTitle');

    // Section Title Text
    pdfInstance.setFontSize(pdfTypoSizes.sectionTitle);
    pdfInstance.setTextColor(...rgbArrayColors.black);
    pdfInstance.text(sectionText, 110, 60);

    // Section underscore
    pdfInstance.setDrawColor(0);
    pdfInstance.setFillColor(...rgbArrayColors.primary);
    pdfInstance.rect(109, 63, 53, 1, 'F');

    // Chip Distribution Table
    pdfInstance.autoTable({
        startY: 69,
        head: [
            [
                translations('pdf.chipDistribution.table.headers.color'),
                translations('pdf.chipDistribution.table.headers.value'),
                translations('pdf.chipDistribution.table.headers.each'),
                translations('pdf.chipDistribution.table.headers.quantity'),
            ],
        ],
        body: chipDistribution.reduce(
            (bodyAcc, { color, value, eachPlayer, quantity }) => [
                ...bodyAcc, 
                [
                    color.charAt(0).toUpperCase() + color.substring(1), 
                    value, 
                    eachPlayer,
                    quantity
                ]
            ]
        , []),
        margin: {
            left
        },
        headStyles: {
            fillColor: rgbArrayColors.primary,
            overflow: 'visible',
        },
        columnStyles: {
            0: {
                cellWidth: 24
            },
            1: {
                cellWidth: 24
            },
            2: {
                cellWidth: 22,
                halign: 'center'
            },
            3: {
                cellWidth: 25,
                halign: 'center'
            }
        },
        styles: {
            ...getTableLatoFont(pdfInstance)
        }
    });
}

const drawBlindStructure = (pdfInstance, translations, blindStructure, startY, left) => {
    // Text/Data
    const sectionText = translations('results.blindStructure.cardTitle');

    // Section Title Text
    pdfInstance.setFontSize(pdfTypoSizes.sectionTitle);
    pdfInstance.setTextColor(...rgbArrayColors.black);
    pdfInstance.text(sectionText, 110, startY);

    // Section underscore
    pdfInstance.setDrawColor(0);
    pdfInstance.setFillColor(...rgbArrayColors.primary);
    pdfInstance.rect(109, startY + 3, 53, 1, 'F');

    // Blind Structure Table
    pdfInstance.autoTable({
        startY: startY + 9,
        head: [
            [
                translations('results.blindStructure.table.headers.smallBlind'),
                translations('results.blindStructure.table.headers.bigBlind'),
                translations('results.blindStructure.table.headers.time'),
            ],
        ],
        body: blindStructure.reduce(
            (bodyAcc, { smallBlind, bigBlind, time }) => [
                ...bodyAcc, 
                [
                    smallBlind, 
                    bigBlind, 
                    convertToTime(time)
                ]
            ]
        , []),
        margin: {
            left
        },
        headStyles: {
            fillColor: rgbArrayColors.primary,
            overflow: 'visible',
        },
        columnStyles: {
            2: {
                cellWidth: 30
            }
        },
        styles: {
            ...getTableLatoFont(pdfInstance)
        }
    });
}

const addLatoFont = (pdfInstance) => {
    jsPDFAddLatoNormal(pdfInstance);
    jsPDFAddLatoBold(pdfInstance);
    pdfInstance.setFont('Lato');

    const getFontFn = pdfInstance.internal.getFont.bind(pdfInstance);
    pdfInstance.internal.getFont = () => {
        const font = getFontFn();
        const unicodeObj = { Unicode: { widths: {}, kerning: {}} };
        const hasUnicode = font.metadata && font.metadata.Unicode
        return hasUnicode ? font : { ...font, metadata: { ...font.metadata, ...unicodeObj } }
    }
}

const getTableLatoFont = (pdfInstance) => {
    const latoFontExist = pdfInstance.getFontList().hasOwnProperty('Lato');
    return latoFontExist ? { font: 'Lato' } : {};
}

const eligibleForCustomFont = () => {
    return (
        typeof jsPDFAddLatoNormal === 'function' &&
        typeof jsPDFAddLatoBold === 'function' &&
        /Edge/.test(navigator.userAgent) === false
    )
}

 const downloadResults = (downloadableData, translations, language, currency) => {
    const date = new Date().toString();
    const doc = new jsPDF();

    // Custom Font
    if (eligibleForCustomFont()) {
        addLatoFont(doc);
    }

    // Header
    drawHeader(doc, translations);

    // Body of document - User selections
    drawUserSelection(
        doc,
        translations,
        downloadableData.selections
    );

    // Second Row consts
    const secondRowStartY = doc.lastAutoTable.finalY + 15;
    const secondRowLeft = 100;

    // Body of document - Tournament Payouts
    drawPayouts(
        doc,
        translations,
        downloadableData.tournamentPayouts.payoutValues,
        language,
        currency,
        secondRowStartY
    );

    // Body of document - Chip distribution
    drawChipDistribution(
        doc,
        translations,
        downloadableData.chipDistribution,
        secondRowLeft
    );

    // Body of document - Blind Structure
    drawBlindStructure(
        doc,
        translations,
        downloadableData.blindStructure,
        secondRowStartY,
        secondRowLeft
    )

    // Footer
    doc.setFontSize(pdfTypoSizes.text);
    doc.text(date, 12, 292);
    
    // Save file
    doc.save('Poker-Chip-Calc-Results.pdf');
 }

 export {
     downloadResults
 }